<template>  
  <div
    class="offcanvas offcanvas-end"
    tabindex="-1"
    id="examUploadOffcanvas"
    data-bs-backdrop="static"
    aria-labelledby="examUploadOffcanvasLabel"
  >
    <div class="offcanvas-header">
      <button class="btn-back" id="closeBtn" type="button" name="button" data-test="btnCloseOffcanvas" aria-label="Close" @click="doCancel">
        <IconArrowBack/>
        {{ $t('goBack') }}
      </button>
      <h5 class="offcanvas-title d-block" id="offCanvasNewExamLabel">{{ $t('exam.uploadTitle') }}</h5>
    </div>
    <div class="offcanvas-body">
      <p>{{ $t('exam.linkedToExam') }}</p>
      <div class="patient-selected" v-if="exam && exam.patient">
        <p class="">{{ exam.patient.fullName }}</p>
      </div>

      <div class="upload-form">
        <input
          type="file"
          ref="fileInput"
          @change="handleFileChange"
          class="d-none"
          :accept="$utils.getAcceptAttribute()"
        />

        <div
          class="upload-dropzone"
          @click="triggerFileInput"
          :class="{ disabled: isUploading }"
        >
          <div class="file-preview" v-if="!file">
            <i class="fa fa-folder-open fa-3x"></i>
            <h6>{{ $t('exam.clickToSelect') }}</h6>
            <p class="upload-hint">
              {{ $utils.getFormattedAllowedExtensions() }}<br />
              {{ $t('exam.maxFileSize') }}
            </p>
          </div>

          <div class="file-preview" v-else>
            <i
              class="file-icon"
              :class="$utils.getFileIcon(fileType)"
            ></i>
            <h6 class="text-truncate">
              {{ file.name }}
            </h6>
            <div>
              <span class="badge bg-secondary">{{ fileType }}</span>
              <span class="badge bg-light text-dark ms-1">{{ (file.size / 1024 / 1024).toFixed(2) }} MB</span>
            </div>

            <button
              @click.stop="removeFile"
              class="no-btn btn-remove"
              v-if="!isUploading"
            >
              <IconCross />
            </button>
          </div>
        </div>

        <div class="form-floating">
          <textarea id="fileDescription" class="form-control" name="name" rows="3" v-model="description"
                    :placeholder="$t('exam.observations')" data-test="textareaObservations" :disabled="isUploading"></textarea>
          <label for="observation">{{ $t('exam.observations') }}</label>
        </div>

        <div v-if="isUploading" class="mt-4">
          <p class="text-center mb-1">
            <small>{{ $t('exam.sendingToCloud') }}</small>
          </p>
          <div class="progress">
            <div
              class="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
            ></div>
          </div>
        </div>
      </div>
    
      <div class="offcanvas-footer">
        <button type="button"
                class="btn btn-success"
                data-test="btnSave"
                :disabled="!isValid || isUploading"
                @click="doUpload">
          {{ $t('exam.saveFile') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.offcanvas {
  .offcanvas-body {
    display:                              flex;
    flex-direction:                       column;
    gap:                                  26px;

    p {
                                          margin: 0 !important;
    }

    .patient-selected {
      display:                            flex;
      align-items:                        center;
      height:                             55px;
      margin:                             0;
      padding:                            20px;
      border:                             2px solid var(--Brand-Primary);
      border-radius:                      99px !important;
      background:                         var(--Shades-10, #FAFAFA);

      p {
        font-size:                        18px;
        color:                            var(--Brand-Primary);
        font-weight:                      700;
        line-height:                      normal;
        flex:                             1;
        justify-content:                  center;
        letter-spacing:                   0.9px;
        text-align:                       center;
        padding:                          20px 0;
      }
    }

    .upload-hint {
      font-size:                          var(--font-size-12);
      color:                              var(--Shades-50);
    }

    .upload-dropzone {
      position:                           relative;
      transition:                         all 0.2s ease;
      text-align:                         center;
      padding:                            20px;
      border:                             1px solid var(--Shades-60-Border);
      border-radius:                      12px;
      cursor:                             pointer;

      h5, h6 {
        font-size:                        var(--font-size-16);
        color:                            var(--Brand-Primary);
      }

      .file-preview {
        justify-content:                  center;
        flex-direction:                   column;
        align-items:                      center;
        display:                          flex;
        gap:                              5px;
        flex-wrap:                        wrap;

        h6 {
          width:                          100%;
          margin:                         0;
        }

        span {
          font-size:                      var(--font-size-12);
        }

        .file-icon {
          font-size:                      48px;
          margin-bottom:                  8px;
        }
      }

      &:hover:not(.disabled) {
        border-color:                     var(--Brand-Primary);
      }

      &.disabled {
        opacity:                          0.6;
        cursor:                           not-allowed;
      }

      .btn-remove {
        position:                         absolute;
        top:                              10px;
        right:                            18px;
        width:                            30px !important;
        height:                           30px !important;
      }
    }
  }

  .btn {
    font-size:                            var(--font-size-14);
    font-weight:                          700;
    line-height:                          normal;
    letter-spacing:                       4.2px;
    text-align:                           center;
    text-transform:                       uppercase;
    width:                                100%;
    margin:                               0;
    padding:                              20px;
  }

  .offcanvas-footer {
    display:                              flex;
    flex-direction:                       column;
    gap:                                  26px;

    .btn-success {
      color:                              var(--Shades-W, #FFF);
    }
  }

  i {
    color:                                var(--Brand-Primary);
    margin: 0;
  }

  .upload-form {
    display:                              flex;
    flex-direction:                       column;
    gap:                                  26px;
  }
}

@media screen and (min-width: 992px) and (max-width: 1365px) {
  .offcanvas {
    .offcanvas-body {
      justify-content:                    flex-start;
    }
    .upload-form {
      gap:                                13px !important;
    }
  }
}
</style>
  
<script>
  export default {
    name: "ExamUploadOffcanvas",
    data: () => ({
      exam: {},
      currentExamId: null,
      file: null,
      fileType: null,
      description: "",
      isUploading: false,
      offcanvas_instance: null,
      resolvePromise: null,
      rejectPromise: null,
    }),
    computed: {
      isValid() {
        return this.file;
      },
    },
    methods: {
      show(options = {}) {
        this.resetForm();
        
        this.exam = options.exam || {};
        this.currentExamId = this.exam.id || this.exam._id;
  
        const el = document.getElementById("examUploadOffcanvas");
        if (el) {
          this.offcanvas_instance = new bootstrap.Offcanvas(el);
          this.offcanvas_instance.show();
        }
  
        return new Promise((resolve, reject) => {
          this.resolvePromise = resolve;
          this.rejectPromise = reject;
        });
      },
  
      async doUpload() {
        if (!this.currentExamId || this.currentExamId === 'undefined') {
          this.$message.popup(this, 'request_error', 'operationFail', 'error');
          return;
        }

        this.isUploading = true;
        try {
          const signedData = await this.$examDataService.getUploadUrlOthers({
            clinicID: this.exam.clinic,
            examId: this.currentExamId,
            fileType: this.fileType,
            fileName: this.file.name,
            fileSize: this.file.size
          });

          const { uploadUrl, uploadFields, s3Key, uuid, extension } = signedData;

          const paramsUploadUrl = {
            uploadUrl,
            uploadFields, 
            file: this.file,
            fileType: this.fileType
          } 
          await this.$examDataService.uploadFileUrl(paramsUploadUrl);

          const payload = {
            clinicID: this.exam.clinic,
            examId: this.currentExamId,
            type: this.fileType,
            description: this.description,
            uuid,
            s3Key,
            extension,
            originalName: this.file.name
          };

          const response = await this.$examDataService.saveOthers(payload);
          
          if (response.status === "ok" || response.examData) {
            this.$message.popup(this, 'exam.uploadSuccess', "operationSuccess", "success");
            if (this.offcanvas_instance) this.offcanvas_instance.hide();
            if (this.resolvePromise) this.resolvePromise(true);
          }
        } catch (error) {
          const errorData = error.response?.data;

          if (errorData?.errorMessage === 'fileLimitReached') {
            this.$message.popup(this, 'exam.fileLimitReached', "operationWarning", "warn");
            return;
          } 
          
          // Check if the error originated from S3 (which returns XML) and if it's a size exceeded error
          if (typeof errorData === 'string' && errorData.includes('<Code>EntityTooLarge</Code>')) {
            this.$message.popup(this, 'exam.errorFileSize', "operationWarning", "warn");
            return;
          }

          this.$message.popup(this, 'request_error', 'operationFail', 'error');
        } finally {
          this.isUploading = false;
        }
      },
  
      resetForm() {
        this.exam = {};
        this.currentExamId = null;
        this.file = null;
        this.fileType = null;
        this.description = "";
        this.isUploading = false;
        if (this.$refs.fileInput) this.$refs.fileInput.value = "";
      },
  
      triggerFileInput() {
        if (!this.isUploading) {
          this.$refs.fileInput.click();
        }
      },
  
      handleFileChange(event) {
        const selectedFile = event.target.files[0];
        if (!selectedFile) return;
  
        const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB em bytes
        if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
          this.$message.popup(this, 'exam.errorFileSize', "operationWarning", "warn");
          return;
        }
  
        const type = this.determineFileType(selectedFile.name);
        if (!type) {
          this.$message.popup(this, 'exam.errorFileType', "operationWarning", "warn");
          return;
        }
  
        this.file = selectedFile;
        this.fileType = type;
      },
  
      determineFileType(filename) {
        const ext = filename.split(".").pop().toLowerCase();
        if (["pdf"].includes(ext)) 
          return "OTHER_PDF";
        if (["doc", "docx", "txt"].includes(ext)) 
          return "OTHER_DOC";
        if (["png", "jpg", "jpeg", "webp", "gif"].includes(ext))
          return "OTHER_IMAGE";
        if (["mp4", "avi", "mpeg", "mpg", "mkv"].includes(ext)) 
          return "OTHER_VIDEO";
        if (["dcm", "dicom"].includes(ext)) 
          return "OTHER_DICOM";

        return null;
      },
  
      removeFile() {
        this.file = null;
        this.fileType = null;
        this.$refs.fileInput.value = "";
      },
  
      doCancel() {
        if (this.isUploading) return;
        if (this.offcanvas_instance) this.offcanvas_instance.hide();
        if (this.resolvePromise) this.resolvePromise(false);
      },
    },
  };
</script>